import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Icono, IconBubble, Avatar } from "./iconos";

const VERDE = "#7BC74D";

const COLUMNAS = [
    { key: "nuevo",          label: "Nuevo",       color: "#1877F2", chip: "Frío",       chipBg: "#e8f0fe", chipColor: "#1877F2" },
    { key: "contactado",     label: "Contactado",  color: "#f0a500", chip: "Tibio",      chipBg: "#fff8e1", chipColor: "#f0a500" },
    { key: "en_seguimiento", label: "Calificado",  color: "#17a2b8", chip: "Calificado", chipBg: "#e0f7fa", chipColor: "#17a2b8" },
    { key: "demo",           label: "Demo",        color: "#7c3aed", chip: "Demo agendada", chipBg: "#f3e8ff", chipColor: "#7c3aed" },
    { key: "cerrado",        label: "Cerrado",     color: VERDE,     chip: "Ganado",     chipBg: "#e8f5e0", chipColor: "#3a7d1e" },
];

const ESTADO_LABEL = {
    nuevo:          { label: "Nuevo",         color: "#1877F2" },
    contactado:     { label: "Contactado",    color: "#f0a500" },
    en_seguimiento: { label: "Calificado",    color: "#17a2b8" },
    cerrado:        { label: "Demo agendada", color: "#7c3aed" },
    sin_interes:    { label: "Rechazado",     color: "#aaa" },
};

const fmtNum = (n, dec = 0) =>
    n == null ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

function PctBadge({ pct }) {
    if (pct == null) return <span style={{ fontSize: "0.72rem", color: "#bbb" }}>—</span>;
    const up = pct >= 0;
    return (
        <span style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: up ? "#3a7d1e" : "#c0392b",
        }}>
            {up ? "+" : ""}{pct}%
            <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 4 }}>vs período anterior</span>
        </span>
    );
}

function KpiCard({ icono, label, value, pct, color }) {
    const c = color || VERDE;
    return (
        <div className="col-6 col-md-4 col-lg-2 mb-3">
            <div className="card h-100 border-0 shadow-sm">
                <div className="card-body py-3 px-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <IconBubble color={c} size={32}>{icono}</IconBubble>
                        <span className="text-muted" style={{ fontSize: "0.74rem", fontWeight: 500 }}>{label}</span>
                    </div>
                    <div style={{ fontSize: "1.55rem", fontWeight: 700, color: "#222", lineHeight: 1.1, marginBottom: 4 }}>
                        {value}
                    </div>
                    <PctBadge pct={pct} />
                </div>
            </div>
        </div>
    );
}

export default function ZTLeads() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [leads, setLeads] = useState([]);
    const [busqueda, setBusqueda] = useState("");

    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) { navegacion("/"); return; }
            try {
                const r = await fetch(process.env.REACT_APP_API_ZT_LEADS, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) { navegacion("/"); return; }
                if (r.ok) { setLeads(await r.json()); setAutorizado(true); }
            } catch { navegacion("/"); }
            finally { setCargando(false); }
        };
        init();
    }, [navegacion]);

    if (cargando) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="spinner-border" style={{ color: VERDE }} />
        </div>
    );
    if (!autorizado) return null;

    const leadsFiltrados = busqueda.trim()
        ? leads.filter(l =>
            [l.nombre, l.empresa, l.email, l.telefono, l.campaignId]
                .some(v => v?.toLowerCase().includes(busqueda.toLowerCase()))
          )
        : leads;

    // KPIs
    const total = leadsFiltrados.length;
    const nuevos       = leadsFiltrados.filter(l => l.estado === "nuevo").length;
    const contactados  = leadsFiltrados.filter(l => ["contactado", "en_seguimiento", "cerrado"].includes(l.estado)).length;
    const calificados  = leadsFiltrados.filter(l => ["en_seguimiento", "cerrado"].includes(l.estado)).length;
    const rechazados   = leadsFiltrados.filter(l => l.estado === "sin_interes").length;
    const activados    = leadsFiltrados.filter(l => l.estado === "cerrado").length;

    const tasaContacto = total > 0 ? Math.round(contactados / total * 100 * 10) / 10 : 0;
    const tasaCierre   = total > 0 ? Math.round(activados   / total * 100 * 10) / 10 : 0;
    const valorEstimado = activados * 850;

    // Últimos 5 leads
    const ultimos = [...leadsFiltrados]
        .sort((a, b) => new Date(b.fechaRecibido) - new Date(a.fechaRecibido))
        .slice(0, 5);

    // Acciones sugeridas: leads más antiguos sin avanzar
    const ahora = new Date();
    const accionesSugeridas = leadsFiltrados
        .filter(l => l.estado === "nuevo" || l.estado === "contactado")
        .map(l => {
            const horas = (ahora - new Date(l.fechaRecibido)) / 36e5;
            const prioridad = horas > 48 ? "alta" : horas > 24 ? "media" : "baja";
            return { ...l, horas, prioridad };
        })
        .sort((a, b) => b.horas - a.horas)
        .slice(0, 5);

    const PRIORIDAD = {
        alta:  { bg: "#fdecea", color: "#c0392b", label: "Prioridad alta" },
        media: { bg: "#fff3cd", color: "#856404", label: "Prioridad media" },
        baja:  { bg: "#e9ecef", color: "#6c757d", label: "Prioridad baja" },
    };

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#222" }}>Leads y CRM</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Gestioná tus oportunidades y convertí más eventos en ventas.
                    </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <input
                        className="form-control form-control-sm"
                        style={{ maxWidth: 240 }}
                        placeholder="Buscar lead, campaña..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                    />
                    <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.78rem" }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: VERDE }} />
                        hace 5 min
                    </span>
                </div>
            </div>

            {/* KPIs */}
            <div className="row g-2 mb-3">
                <KpiCard icono={<Icono.UserPlus />}     label="Leads nuevos"            value={nuevos}      pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Phone />}        label="Contactados"             value={contactados} pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Star />}         label="Calificados"             value={calificados} pct={null} color={VERDE} />
                <KpiCard icono={<Icono.XCircle />}      label="Rechazados"              value={rechazados}  pct={null} color={VERDE} />
                <KpiCard icono={<Icono.CheckCircle />}  label="Activados"               value={activados}   pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Clock />}        label="Tiempo medio respuesta"  value="—"           pct={null} color={VERDE} />
            </div>

            {/* Kanban */}
            <div className="d-flex gap-3 overflow-auto pb-2 mb-3" style={{ minHeight: 360 }}>
                {COLUMNAS.map(col => {
                    const leadsCol = leadsFiltrados.filter(l => {
                        if (col.key === "demo") return false;
                        return l.estado === col.key;
                    });
                    return (
                        <div key={col.key} style={{ minWidth: 250, flex: "0 0 250px" }}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: col.color }} />
                                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: col.color }}>{col.label}</span>
                                <span className="text-muted" style={{ fontSize: "0.78rem" }}>({leadsCol.length})</span>
                                <button className="btn btn-sm p-0 ms-auto" style={{ fontSize: "0.95rem", color: "#aaa", lineHeight: 1 }}>+</button>
                            </div>
                            <div style={{ maxHeight: 540, overflowY: "auto", paddingRight: 4 }}>
                                {leadsCol.length === 0 && (
                                    <div className="text-muted text-center py-3" style={{ fontSize: "0.75rem" }}>—</div>
                                )}
                                {leadsCol.map(l => (
                                    <div key={l.id} className="card border-0 shadow-sm mb-2" style={{ borderRadius: 10 }}>
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="d-flex gap-2 align-items-center" style={{ minWidth: 0 }}>
                                                    <Avatar nombre={l.nombre} size={32} />
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                                                            {l.nombre || "—"}
                                                        </div>
                                                        <div style={{ fontSize: "0.68rem", color: "#999", display: "flex", alignItems: "center", gap: 4 }}>
                                                            <span style={{ fontSize: "0.6rem" }}>●●</span> Meta
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="btn btn-sm p-0" style={{ color: "#aaa", fontSize: "1rem", lineHeight: 1 }}>⋮</button>
                                            </div>
                                            <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                                                Campaña: <span style={{ color: "#444" }}>{l.campaignId ? `Campaña ${l.campaignId.slice(-6)}` : "Sin asignar"}</span>
                                            </div>
                                            <span style={{
                                                display: "inline-block",
                                                fontSize: "0.68rem",
                                                fontWeight: 600,
                                                padding: "2px 8px",
                                                borderRadius: 12,
                                                backgroundColor: col.chipBg,
                                                color: col.chipColor,
                                            }}>
                                                {col.chip}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Últimos leads + Tasas + Acciones */}
            <div className="row g-3">
                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">Últimos 5 leads recibidos</h6>
                                <button className="btn btn-sm" style={{ fontSize: "0.75rem", color: VERDE, fontWeight: 600 }}>Ver todos →</button>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid #eee" }}>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999", paddingBottom: 10 }}>Fecha</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Nombre</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ultimos.length === 0 && (
                                            <tr><td colSpan={3} className="text-center text-muted py-4">Sin leads</td></tr>
                                        )}
                                        {ultimos.map(l => {
                                            const fecha = new Date(l.fechaRecibido);
                                            const fechaStr = `${fecha.toLocaleDateString("es-AR")}, ${fecha.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}`;
                                            const iniciales = (l.nombre || "—").split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join(". ") + ".";
                                            const e = ESTADO_LABEL[l.estado] || { label: l.estado, color: "#aaa" };
                                            return (
                                                <tr key={l.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                    <td style={{ fontSize: "0.75rem", color: "#666", padding: "10px 0" }}>{fechaStr}</td>
                                                    <td style={{ fontSize: "0.78rem", fontWeight: 500 }}>{iniciales}</td>
                                                    <td>
                                                        <span style={{
                                                            display: "inline-block",
                                                            fontSize: "0.68rem",
                                                            fontWeight: 600,
                                                            padding: "3px 10px",
                                                            borderRadius: 12,
                                                            backgroundColor: `${e.color}26`,
                                                            color: e.color,
                                                        }}>{e.label}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-3">
                    <div className="d-flex flex-column gap-3 h-100">
                        {[
                            { label: "Tasa de contacto", value: `${fmtNum(tasaContacto, 1)}%`, pct: 8.2 },
                            { label: "Tasa de cierre",   value: `${fmtNum(tasaCierre, 1)}%`,   pct: 12.5 },
                            { label: "Valor estimado",   value: `US$ ${fmtNum(valorEstimado, 0)}`, pct: 15.7 },
                        ].map((m, i) => (
                            <div key={i} className="card border-0 shadow-sm">
                                <div className="card-body py-2 px-3">
                                    <div className="text-muted mb-1" style={{ fontSize: "0.74rem" }}>{m.label}</div>
                                    <div className="d-flex justify-content-between align-items-end">
                                        <strong style={{ fontSize: "1.3rem", color: "#222" }}>{m.value}</strong>
                                        <svg width="50" height="20" viewBox="0 0 50 20">
                                            <polyline points="0,15 10,12 20,14 30,8 40,10 50,4" fill="none" stroke={VERDE} strokeWidth="2" />
                                        </svg>
                                    </div>
                                    <PctBadge pct={m.pct} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-12 col-lg-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3">Acciones sugeridas</h6>
                            {accionesSugeridas.length === 0 ? (
                                <p className="text-muted" style={{ fontSize: "0.82rem" }}>No hay acciones pendientes.</p>
                            ) : accionesSugeridas.map(l => {
                                const p = PRIORIDAD[l.prioridad];
                                return (
                                    <div key={l.id} className="d-flex justify-content-between align-items-start gap-2 mb-3">
                                        <div className="d-flex gap-2 align-items-start" style={{ minWidth: 0, flex: 1 }}>
                                            <IconBubble color="#999" size={32}><Icono.Clock /></IconBubble>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#222" }}>
                                                    {l.nombre || "—"}
                                                </div>
                                                <div style={{ fontSize: "0.72rem", color: "#666" }}>
                                                    {l.estado === "nuevo" ? "Sin contactar — primer toque pendiente" : "Seguimiento inicial pendiente"}
                                                </div>
                                                <div style={{ fontSize: "0.68rem", color: "#999" }}>
                                                    Hace {Math.round(l.horas)}h
                                                </div>
                                            </div>
                                        </div>
                                        <span style={{
                                            fontSize: "0.65rem",
                                            fontWeight: 600,
                                            padding: "2px 8px",
                                            borderRadius: 12,
                                            backgroundColor: p.bg,
                                            color: p.color,
                                            whiteSpace: "nowrap",
                                        }}>{p.label}</span>
                                    </div>
                                );
                            })}
                            <button className="btn btn-sm w-100 mt-2" style={{
                                fontSize: "0.78rem",
                                border: "1px solid #ddd",
                                color: "#555",
                                fontWeight: 500,
                            }}>
                                Ver todas las acciones →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
